import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Table,
	Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { FormAlert, useForm } from '@/libs/antd/form';
import {
	addFRMOfficeObservationSalesMethod,
	deleteFRMOfficeObservationSalesMethod,
	updateFRMOfficeObservationSalesMethod,
} from '@/libs/fleet-api/services/visit-reports';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { useMutation } from '@tanstack/react-query';
import { useFRMVisitReportPageContext } from './VisitReportOfficePage';
import { OfficeObservationSalesMethod } from '@/libs/fleet-api/services/types';
import { useSelectSalesMethod } from '@/libs/fleet-api/hooks/master-data';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import { DefaultOptionType } from 'antd/es/select';
import { useModals } from '@/components/modals/ModalProvider';

type OfficeObservationSalesMethodData =
	OfficeObservationSalesMethod<'payload'> &
		Pick<OfficeObservationSalesMethod, 'salesMethod'>;

type VisitReportSalesMethodForm = {
	input?: OfficeObservationSalesMethodData;
	lists: OfficeObservationSalesMethodData[];
};

const FRMVisitReportSalesMethods: FC = () => {
	const modalAPI = useModals();
	const { surveyInformation, officeObservation } =
		useFRMVisitReportPageContext();
	const { marketingAspect } = officeObservation ?? {};
	const { form, value, fieldName } = useForm<VisitReportSalesMethodForm>();
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	const addFRMOfficeObservationSalesMethodMutation = useMutation({
		mutationFn: addFRMOfficeObservationSalesMethod,
	});

	const updateFRMOfficeObservationSalesMethodMutation = useMutation({
		mutationFn: updateFRMOfficeObservationSalesMethod,
	});

	const deleteFRMOfficeObservationSalesMethodMutation = useMutation({
		mutationFn: deleteFRMOfficeObservationSalesMethod,
	});

	const selectSalesMethod = useSelectSalesMethod();

	const getSelectSalesMethod = () => {
		const salesMethod = form.getFieldValue(
			fieldName(['input', 'salesMethod']),
		) as MasterData | null;

		if (!salesMethod || selectSalesMethod.options?.length) {
			return selectSalesMethod.options;
		}

		return [
			{
				value: salesMethod.id,
				label: salesMethod.value,
			},
		] as DefaultOptionType[];
	};

	return (
		<Form.Provider>
			<Typography.Title level={3}>Metode Penjualan</Typography.Title>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						lists:
							marketingAspect?.salesMethods?.map((record) => {
								return {
									methodDetail: record.methodDetail,
									salesMethod: record.salesMethod,
									salesMethodId: record.salesMethod.id,
									surveyId: record.surveyId,
									id: record.id,
								};
							}) ?? [],
					} as VisitReportSalesMethodForm
				}
				onFinish={async ({ input, lists }) => {
					if (modal?.type === 'edit') {
						await updateFRMOfficeObservationSalesMethodMutation.mutateAsync({
							id: input?.id,
							surveyId: surveyInformation!.id!,
							methodDetail: input!.methodDetail,
							salesMethodId: input!.salesMethodId,
						});
						form.setFieldsValue({
							lists: lists.map((list, index) => {
								return index === modal.index ? input! : list;
							}),
						});
					} else {
						const res =
							await addFRMOfficeObservationSalesMethodMutation.mutateAsync({
								surveyId: surveyInformation!.id!,
								methodDetail: input!.methodDetail,
								salesMethodId: input!.salesMethodId,
							});
						form.setFieldsValue({
							lists: [...lists, { ...input!, id: res.data.id }],
						});
					}
					setModal(null);
				}}
				style={{ marginBottom: 30 }}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['lists'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as VisitReportSalesMethodForm;
						const dataSource =
							values?.lists?.map((list, key?) => {
								return { ...list, key };
							}) ?? [];

						return (
							<Fragment>
								<Row style={{ display: 'flex', justifyContent: 'end' }}>
									<Button
										style={{
											color: '#1557BC',
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											borderColor: '#1557BC',
											fontWeight: 700,
											marginBottom: 20,
										}}
										loading={
											addFRMOfficeObservationSalesMethodMutation.isPending ||
											updateFRMOfficeObservationSalesMethodMutation.isPending
										}
										onClick={() => setModal({ type: 'add' })}
										icon={'+'}
									>
										Add Another
									</Button>
								</Row>
								<Table
									bordered
									pagination={false}
									dataSource={dataSource}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete =
																		await modalAPI.modal.confirm({
																			title: 'delete this data?',
																		});

																	if (confirmDelete) {
																		await deleteFRMOfficeObservationSalesMethodMutation.mutateAsync(
																			record.id!,
																		);
																		form.setFieldsValue({
																			lists: values.lists.filter(
																				(_, listIndex) => listIndex !== index,
																			),
																		});
																	}
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
											width: 1,
										},
										{
											title: 'Metode Penjualan',
											render: (_, record) => record.salesMethod.value ?? '-',
										},
										{
											title: 'Detail Metode Penjualan',
											render: (_, record) => record.methodDetail ?? '-',
										},
									]}
								/>
								<FormAlert
									alerts={
										!value?.lists?.length
											? ['Metode Penjualan wajib diisi']
											: []
									}
								/>
								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										form.resetFields([fieldName('input')]);
									}}
									onCancel={() => {
										setModal(null);
									}}
									onOk={() => {
										form.submit();
									}}
								>
									<FleetTitle>Add Metode Penjualan</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'surveyId'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'salesMethod'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'salesMethodId'])}
												label="Metode Penjualan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Select
													{...selectSalesMethod}
													onSelect={(value, option) => {
														selectSalesMethod.onSelect!(value, option);
														form.setFieldValue(
															fieldName(['input', 'salesMethod']),
															option.data,
														);
													}}
													options={getSelectSalesMethod()}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											{form.getFieldValue(
												fieldName(['input', 'salesMethod']),
											) && (
												<Form.Item
													name={fieldName(['input', 'methodDetail'])}
													label={`Jika ${form.getFieldValue(fieldName(['input', 'salesMethod']))?.value}, Sebutkan`}
													labelCol={{ span: 10 }}
													rules={[rule.required()]}
												>
													<Input />
												</Form.Item>
											)}
										</Col>
									</Row>
									<Divider />
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
			<Divider />
		</Form.Provider>
	);
};

export default FRMVisitReportSalesMethods;
